"use server";

import { getRequestEvent } from "solid-js/web";
import { redirect } from "@solidjs/router";
import { serverRepo } from "~/server/apis/server_repo";
import { getStaticJson } from "~/server/static";
import { CategoryResult } from "~/server/types/category";
import { Product, SearchResults } from "~/server/types/search";
import { HomePageSectionSeparator, Separator } from "~/server/types/separator";
import { Metadata } from "~/server/types/groups";
import type { BannerV3 } from "~/data/banners";

export type SectionData = {
  title: string;
  bgColor: string;
  brandList: Product[];
};

export type HomeWebstoreTelescopeRouteData = {
  categories: CategoryResult;
  brands: Product[];
  sections: SectionData[];
  footNotes: { content: string };
  metadata: Metadata;
  banners: BannerV3[];
};

export async function getHomeWebstoreTelescopeRouteData(): Promise<HomeWebstoreTelescopeRouteData> {
  const requestEvent = getRequestEvent();
  const mode = requestEvent?.locals.mode ?? "hubble";
  const isSessionPresent = requestEvent?.locals.sid;
  const clientId = requestEvent?.locals.clientId;

  if (mode === "squid") {
    throw redirect("/login");
  }

  if (mode === "rnr" && !isSessionPresent) {
    throw redirect("/login");
  }

  // Fetch all data in parallel
  const [
    categories,
    allBrands,
    homePageSectionSeparator,
    footNotes,
    metadata,
    banners,
  ] = await Promise.all([
    serverRepo.getCategories(),
    serverRepo.getProductSearch({
      query: "",
      pagination: {
        limit: 200,
        pageNo: 0,
      },
    }),
    getStaticJson("home_page/home_separators.json") as Promise<HomePageSectionSeparator>,
    getStaticJson("home_page/footnotes.json") as Promise<{ content: string }>,
    getStaticJson("home_page/metadata.json") as Promise<Metadata>,
    serverRepo
      .getBanners()
      .then((res) => res.banners ?? [])
      .catch(() => [] as BannerV3[]),
  ]);

  let separators = homePageSectionSeparator.separators;

  // Filter out sections if needed (e.g., for specific clients)
  if (clientId?.startsWith("raisoni")) {
    separators = separators.filter((separator) => separator.id !== "1");
  }

  // Get all active brands
  const allActiveBrandProducts =
    allBrands?.data?.[0]?.products?.filter(
      (product) => product.voucherProduct.status === "ACTIVE"
    ) || [];

  // Map separators to section data with brand products
  const sectionSeparators: SectionData[] = separators
    .map((separator) => {
      const products = separator.brands
        .map((brandKey) =>
          allActiveBrandProducts.find((p) => p.brandKey === brandKey)
        )
        .filter((product): product is Product => Boolean(product));

      return {
        title: separator.heading,
        bgColor: separator.color_code,
        brandList: products,
      };
    })
    .filter((section) => section.brandList.length >= 3);

  return {
    categories,
    brands: allActiveBrandProducts,
    sections: sectionSeparators,
    footNotes,
    metadata,
    banners,
  };
}
